import type { UAEntityTableComponentType } from '@unifyapps/defs/blocks/Table/UAEntityTable/types';
import { getAggregateQueryKey } from '@unifyapps/network/generated/aggregation-rest-api/aggregation-rest-api';
import type { AggregationQuery } from '@unifyapps/network/generated/models/aggregationQuery';
import type { Filter } from '@unifyapps/network/generated/models/filter';
import { Op } from '@unifyapps/network/generated/models/op';
import type { Projection } from '@unifyapps/network/generated/models/projection';
import type { Sort } from '@unifyapps/network/generated/models/sort';
import compact from 'lodash/compact';
import { useMemo } from 'react';
import { AggregationQueryBuilder } from '../utils/builders/aggregationQuery';

interface UseAggregationQueryBuilderParams {
  filter?: Filter | null;
  search?: string | null;
  sort?: Sort[] | null;
  timeFilter?: UAEntityTableComponentType['metadata']['timeFilter'] | null;
}

export const adaptSearchToFilter = (
  search?: string | null,
  searchField?: string,
): Filter | undefined => {
  return search ? { op: 'ICONTAINS', field: searchField ?? 'lcName', values: [search] } : undefined;
};

// using only group and entityType to invalidate the query, since other fields can be computed
export const getAggregationQueryKeyToInvalidate = (
  tableConfiguration: UAEntityTableComponentType,
) =>
  getAggregateQueryKey({
    group: tableConfiguration.metadata.group,
    entityType: tableConfiguration.metadata.entityType,
  });

export const buildAggregationQuery = (
  tableConfiguration: UAEntityTableComponentType,
  { filter, search, sort, timeFilter }: UseAggregationQueryBuilderParams = {},
): AggregationQuery => {
  const aggregationQuery = new AggregationQueryBuilder();

  aggregationQuery.withEntityType(tableConfiguration.metadata.entityType);
  aggregationQuery.withGroup(tableConfiguration.metadata.group);

  if (tableConfiguration.metadata.includeTotalHits) {
    aggregationQuery.withIncludeTotalHits(tableConfiguration.metadata.includeTotalHits);
  }

  // need to verify this change
  const projections: Projection[] = [...(tableConfiguration.metadata.projections || [])];

  const useAlias = tableConfiguration.metadata.useAlias;

  tableConfiguration.columns.forEach((schemaColumn) => {
    if (schemaColumn.fieldKey) {
      projections.push({
        name:
          typeof schemaColumn.fieldKey === 'string'
            ? schemaColumn.fieldKey
            : //@ts-expect-error -- need to convert prop type to DataTableComponentType
              //eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- need to convert prop type to DataTableComponentType
              (schemaColumn.fieldKey?.path as string),
        ...(schemaColumn.aggregationFunction
          ? { aggregationFunction: schemaColumn.aggregationFunction }
          : {}),
        ...(useAlias
          ? {
              alias: schemaColumn.alias?.[schemaColumn.fieldKey],
            }
          : {}),
      });
    }
    if (schemaColumn.fieldKeys) {
      schemaColumn.fieldKeys.forEach((fieldKey) => {
        projections.push({
          name: fieldKey,
          ...(useAlias ? { alias: schemaColumn.alias?.[fieldKey] } : {}),
          ...(schemaColumn.aggregationFunction
            ? { aggregationFunction: schemaColumn.aggregationFunction }
            : {}),
        });
      });
    }

    if (tableConfiguration.metadata.identifier) {
      const isIdentifierAddedToProjections = projections.some(
        (projection) => projection.alias === tableConfiguration.metadata.identifier,
      );

      if (!isIdentifierAddedToProjections) {
        projections.push({
          name: tableConfiguration.metadata.identifier,
          ...(useAlias ? { alias: tableConfiguration.metadata.identifier } : {}),
        });
      }
    }
  });

  if (filter || tableConfiguration.metadata.filter || search) {
    /*
      Fix: Query Invalidation Bug Due to Filter Order

      References:
      https://github.com/TanStack/query/blob/main/packages/query-core/src/utils.ts#L123
      https://github.com/TanStack/query/blob/main/packages/query-core/src/utils.ts#L228

      Problem:
      When rendering a block with a default filter (like DataQualityManagerBlock in block.ts which sets a default filter in metadata) 
      and then applying an additional filter from the UI, React Query's invalidation system wasn't working properly.

      Technical Context:
      React Query performs partial comparisons of queryKey objects and invalidates all queries whose queryKey starts with a given prefix. 
      For example:

      - Invalidating ['a', 'b'] will invalidate:
        - ['a', 'b']
        - ['a', 'b', 'c']
        - ['a', 'b', 'c', 'd']

      - But will not invalidate:
        - ['a', 'c', 'b']
        - ['a', 'c', 'b', 'd']

      React Query scans all queries in its cache and invalidates all queryKeys that have the specified prefix in their query key.
      To invalidate our aggregation query used to render the manager table, we create a queryKey based on the block:

      -> invalidateQueries(getAggregationQuery(block))

      This results in a queryKey where the defaultFilters are included, for example:

      const queryKey = [
        { blockId: block.id },
        { defaultFilters: tableConfiguration.metadata.filter },
      ];

      Solution:
      We cannot include userFilters in the queryKey before the defaultFilters, because then the prefix matching would fail when 
      we try to invalidate. We need to build a queryKey whose prefix is static so that React Query can do prefix matching.

      The fix moves the userFilters to the end of the queryKey and brings the default filters to the front. This way, 
      we can invalidate all queries that have the same blockId and defaultFilters.

      This consistent ordering guarantees that query invalidation and cache updates work correctly for all filter combinations, 
      resolving the issues in DataQualityManagerBlock and similar components.
    */
    aggregationQuery.withFilter({
      op: Op.AND,
      values: compact([
        tableConfiguration.metadata.filter,
        filter,
        adaptSearchToFilter(search, tableConfiguration.metadata.searchField),
      ]),
    });
  }

  if (tableConfiguration.metadata.additional) {
    aggregationQuery.withAdditionalParams(tableConfiguration.metadata.additional);
  }

  if (timeFilter) {
    const adaptedTimeFilter = {
      since: timeFilter.since ? new Date(timeFilter.since).getTime() : undefined,
      until: timeFilter.until ? new Date(timeFilter.until).getTime() : undefined,
    };
    aggregationQuery.withTimeFilter(adaptedTimeFilter);
  }

  if (sort || tableConfiguration.metadata.sorts) {
    aggregationQuery.withSorts(sort || tableConfiguration.metadata.sorts || []);
  }

  // evaluate using unique values here if using metadata.projections
  aggregationQuery.withProjections(projections);

  if (tableConfiguration.metadata.id) {
    aggregationQuery.withId(tableConfiguration.metadata.id);
  }

  return aggregationQuery.build();
};

/**
 * Hook to generate the query for the table based on the table configuration.
 * @param tableConfiguration - The configuration for the table, including column properties and metadata
 * @param filter - The filter to apply to the query
 */
export function useAggregationQueryBuilder(
  tableConfiguration: UAEntityTableComponentType,
  { filter, search, sort, timeFilter }: UseAggregationQueryBuilderParams = {},
): AggregationQuery {
  return useMemo(
    () => buildAggregationQuery(tableConfiguration, { filter, search, sort, timeFilter }),
    [filter, search, sort, tableConfiguration, timeFilter],
  );
}
